﻿// CourseContent.js
import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  SafeAreaView,
  Dimensions,
  Alert,
} from 'react-native';
import { VideoView, useVideoPlayer } from 'expo-video';
import { MaterialCommunityIcons } from '@expo/vector-icons';
import { useNavigation, useRoute } from '@react-navigation/native';
import { LinearGradient } from 'expo-linear-gradient';

const CourseContent = () => {
  const navigation = useNavigation();
  const route = useRoute();
  const { course, module } = route.params || {};
  
  const [currentVideo, setCurrentVideo] = useState(course?.videoPreview);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [selectedLesson, setSelectedLesson] = useState(null);
  const [completedLessons, setCompletedLessons] = useState([]);

  const player = useVideoPlayer(null, (p) => {
    if (p) { p.loop = false; }
  });

  // Load the current video into the player whenever it changes.
  useEffect(() => {
    if (!player) return;
    (async () => {
      try {
        if (currentVideo) {
          if (player.replaceAsync) {
            await player.replaceAsync({ uri: currentVideo });
          } else {
            player.replace({ uri: currentVideo });
          }
        }
      } catch (e) {
        // ignore load errors
      }
    })();
  }, [currentVideo]);

  return (
    <SafeAreaView style={styles.container}>
      <LinearGradient
        colors={['#6366f1', '#8b5cf6']}
        style={styles.header}
      >
        <TouchableOpacity onPress={() => navigation.goBack()}>
          <MaterialCommunityIcons name="arrow-left" size={24} color="#ffffff" />
        </TouchableOpacity>
        <Text style={styles.headerTitle} numberOfLines={1}>
          {module?.title || course?.title}
        </Text>
        <View style={{ width: 24 }} />
      </LinearGradient>

      <View style={styles.videoContainer}>
        <VideoView
          player={player}
          style={styles.videoPlayer}
          nativeControls
          contentFit="contain"
          fullscreenOptions={{ enable: true }}
        />
      </View>

      <ScrollView style={styles.content} showsVerticalScrollIndicator={false}>
        <View style={styles.lessonInfo}>
          <Text style={styles.lessonTitle}>
            {selectedLesson?.title || module?.title || 'Select a lesson'}
          </Text>
          <Text style={styles.lessonDescription}>
            {selectedLesson?.description || module?.description || 'Watch the video and complete the exercises below.'}
          </Text>
        </View>

        <View style={styles.lessonsList}>
          {module?.lessons?.map((lesson, index) => (
            <TouchableOpacity
              key={index}
              style={[
                styles.lessonItem,
                completedLessons.includes(lesson.id) && styles.completedLesson,
                selectedLesson?.id === lesson.id && styles.selectedLesson,
              ]}
              onPress={() => {
                setSelectedLesson(lesson);
                setCurrentVideo(lesson.videoUrl || course.videoPreview);
              }}
            >
              <View style={styles.lessonNumber}>
                <Text style={styles.lessonNumberText}>{index + 1}</Text>
              </View>
              <View style={styles.lessonContent}>
                <Text style={styles.lessonItemTitle}>{lesson.title}</Text>
                <Text style={styles.lessonDuration}>{lesson.duration}</Text>
              </View>
              {completedLessons.includes(lesson.id) ? (
                <MaterialCommunityIcons name="check-circle" size={24} color="#10b981" />
              ) : (
                <MaterialCommunityIcons name="play-circle" size={24} color="#6366f1" />
              )}
            </TouchableOpacity>
          ))}
        </View>

        <View style={styles.exercises}>
          <Text style={styles.sectionTitle}>Exercises</Text>
          <TouchableOpacity style={styles.exerciseButton}>
            <MaterialCommunityIcons name="clipboard-text" size={20} color="#6366f1" />
            <Text style={styles.exerciseButtonText}>Practice Quiz</Text>
          </TouchableOpacity>
          <TouchableOpacity style={styles.exerciseButton}>
            <MaterialCommunityIcons name="code-braces" size={20} color="#10b981" />
            <Text style={styles.exerciseButtonText}>Coding Challenge</Text>
          </TouchableOpacity>
          <TouchableOpacity style={styles.exerciseButton}>
            <MaterialCommunityIcons name="forum" size={20} color="#f59e0b" />
            <Text style={styles.exerciseButtonText}>Discussion Questions</Text>
          </TouchableOpacity>
        </View>
      </ScrollView>

      <View style={styles.footer}>
        <TouchableOpacity
          style={[styles.footerButton, styles.previousButton]}
          onPress={() => navigation.goBack()}
        >
          <MaterialCommunityIcons name="chevron-left" size={20} color="#6366f1" />
          <Text style={styles.previousButtonText}>Previous</Text>
        </TouchableOpacity>
        
        <TouchableOpacity
          style={[styles.footerButton, styles.completeButton]}
          onPress={() => {
            if (selectedLesson) {
              setCompletedLessons([...completedLessons, selectedLesson.id]);
              Alert.alert('Success', 'Lesson marked as complete!');
            }
          }}
        >
          <MaterialCommunityIcons name="check-circle" size={20} color="#ffffff" />
          <Text style={styles.completeButtonText}>
            {completedLessons.includes(selectedLesson?.id) ? 'Completed' : 'Mark Complete'}
          </Text>
        </TouchableOpacity>
        
        <TouchableOpacity
          style={[styles.footerButton, styles.nextButton]}
          onPress={() => {
            const lessons = module?.lessons || [];
            const idx = lessons.findIndex((l) => l.id === selectedLesson?.id);
            if (idx >= 0 && idx < lessons.length - 1) {
              const next = lessons[idx + 1];
              setSelectedLesson(next);
              setCurrentVideo(next.videoUrl || course?.videoPreview);
            } else {
              Alert.alert('Module Complete', 'You\u2019ve reached the last lesson in this module.');
            }
          }}
        >
          <Text style={styles.nextButtonText}>Next</Text>
          <MaterialCommunityIcons name="chevron-right" size={20} color="#6366f1" />
        </TouchableOpacity>
      </View>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#ffffff',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    paddingVertical: 12,
  },
  headerTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#ffffff',
    flex: 1,
    textAlign: 'center',
    marginHorizontal: 16,
  },
  videoContainer: {
    height: 250,
    backgroundColor: '#000000',
  },
  videoPlayer: {
    flex: 1,
  },
  content: {
    flex: 1,
    padding: 16,
  },
  lessonInfo: {
    marginBottom: 24,
  },
  lessonTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#1f2937',
    marginBottom: 8,
  },
  lessonDescription: {
    fontSize: 14,
    color: '#6b7280',
    lineHeight: 20,
  },
  lessonsList: {
    marginBottom: 24,
  },
  lessonItem: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 16,
    borderRadius: 12,
    backgroundColor: '#f3f4f6',
    marginBottom: 8,
  },
  completedLesson: {
    backgroundColor: '#d1fae5',
  },
  selectedLesson: {
    backgroundColor: '#dbeafe',
    borderWidth: 1,
    borderColor: '#6366f1',
  },
  lessonNumber: {
    width: 36,
    height: 36,
    borderRadius: 18,
    backgroundColor: '#6366f1',
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 16,
  },
  lessonNumberText: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#ffffff',
  },
  lessonContent: {
    flex: 1,
  },
  lessonItemTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#1f2937',
    marginBottom: 4,
  },
  lessonDuration: {
    fontSize: 12,
    color: '#6b7280',
  },
  exercises: {
    marginBottom: 24,
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#1f2937',
    marginBottom: 16,
  },
  exerciseButton: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 16,
    borderRadius: 12,
    backgroundColor: '#f3f4f6',
    marginBottom: 8,
  },
  exerciseButtonText: {
    fontSize: 16,
    fontWeight: '600',
    color: '#1f2937',
    marginLeft: 12,
  },
  footer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    padding: 16,
    borderTopWidth: 1,
    borderTopColor: '#e5e7eb',
    backgroundColor: '#ffffff',
  },
  footerButton: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderRadius: 8,
  },
  previousButton: {
    backgroundColor: '#f3f4f6',
  },
  previousButtonText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#6366f1',
    marginLeft: 4,
  },
  completeButton: {
    backgroundColor: '#10b981',
  },
  completeButtonText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#ffffff',
    marginLeft: 4,
  },
  nextButton: {
    backgroundColor: '#f3f4f6',
  },
  nextButtonText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#6366f1',
    marginRight: 4,
  },
});

export default CourseContent;